import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import DragIndicator from '@material-ui/icons/DragIndicator';
import ExpandLess from '@material-ui/icons/ExpandLess';
import classNames from 'classnames';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import CollapsibleCard from 'core/components/collapsible-card';
import { Label } from 'core/components/label';
import useStyles from './sortable-sidebar.styles';

// A corrupt or hand-edited value falls back to the default layout instead of crashing the page
const readSavedLayout = (storageKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey));
    return {
      order: Array.isArray(saved?.order) ? saved.order : undefined,
      collapsed: Array.isArray(saved?.collapsed) ? saved.collapsed : undefined
    };
  } catch {
    return {};
  }
};

// CollapsibleCard's 64px bar plus its 3px shadow padding top and bottom
const COLLAPSED_BAR_HEIGHT = 70;

// Cards that render nothing are hidden with CSS but stay in the list, so they can't be drop targets.
const visibleCardsCollision = (args) =>
  closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter((container) => container.rect.current?.height > 0)
  });

// Each card puts its heading at a different depth, so the controls line up with the card's first visible line of
// text (heading, collapsed bar title, or first label) and follow it as the card loads, collapses or resizes.
const useControlsAtFirstTextLine = (itemRef, controlsRef) => {
  useLayoutEffect(() => {
    const item = itemRef.current;
    const align = () => {
      const walker = document.createTreeWalker(item, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) =>
          node.textContent.trim() && node.parentElement.offsetParent ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
      });
      const firstLine = walker.nextNode()?.parentElement.getBoundingClientRect();
      const handle = controlsRef.current.firstElementChild.getBoundingClientRect();
      if (firstLine && handle.height) {
        const top = firstLine.top - item.getBoundingClientRect().top + (firstLine.height - handle.height) / 2;
        // Measured at runtime, so it can't live in the stylesheet
        controlsRef.current.style.top = `${Math.max(0, Math.round(top))}px`;
      }
    };
    align();
    const observer = new ResizeObserver(align);
    observer.observe(item);
    return () => observer.disconnect();
  }, [itemRef, controlsRef]);
};

const SortableCard = ({ card, collapsed, collapsedClassName, itemComponent: Item, onToggleCollapsed }) => {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id
  });
  const itemRef = useRef(null);
  const controlsRef = useRef(null);
  useControlsAtFirstTextLine(itemRef, controlsRef);
  const setItemRef = useCallback(
    (node) => {
      setNodeRef(node);
      itemRef.current = node;
    },
    [setNodeRef]
  );

  return (
    <Item
      ref={setItemRef}
      className={classNames(card.className, classes.item, {
        [classes.dragging]: isDragging,
        [collapsedClassName]: collapsed
      })}
      // dnd-kit moves the card every frame through an inline transform
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <div ref={controlsRef} className={classes.controls}>
        <button
          type="button"
          ref={setActivatorNodeRef}
          className={classNames(classes.control, classes.handle)}
          aria-label={`Move ${card.title}`}
          {...attributes}
          {...listeners}
        >
          <DragIndicator fontSize="small" />
        </button>
        {!collapsed && (
          <button
            type="button"
            className={classes.control}
            aria-label={`Collapse ${card.title}`}
            onClick={onToggleCollapsed}
          >
            <ExpandLess fontSize="small" />
          </button>
        )}
      </div>
      {collapsed && (
        <div className={classes.collapsedBar}>
          <CollapsibleCard
            type={card.dark ? 'secondary' : 'primary'}
            content={<Label type={card.dark ? 'infoSubtitleWhite' : 'infoSubtitle'}>{card.title}</Label>}
            open={false}
            onChange={onToggleCollapsed}
            collapsedSize={COLLAPSED_BAR_HEIGHT}
          >
            {/* Required by CollapsibleCard; the card's real content renders below */}
            {''}
          </CollapsibleCard>
        </div>
      )}
      {/* Collapsed cards stay mounted so their data loading and side effects don't change */}
      <div className={classNames(classes.content, { [classes.hidden]: collapsed })}>{card.node}</div>
    </Item>
  );
};

/**
 * Spike: drag-to-reorder and collapsible sidebar cards, saved per sidebar in localStorage.
 * `cards` is `{ id, title, show, dark?, collapsedByDefault?, rememberCollapsed?, className?, node }[]` in the default
 * order. `dark` styles the collapsed bar to match a dark green card; `rememberCollapsed: false` reopens the card on every
 * page load and whenever it reappears. `collapsedClassName` adds spacing for sidebars whose cards bring their own margins.
 * `itemComponent` wraps each card (default `div`); it must forward its ref.
 */
const SortableSidebar = ({ name, cards, collapsedClassName, itemComponent = 'div' }) => {
  const storageKey = `sidebarLayout:${name}`;
  const [layout, setLayout] = useState(() => {
    const saved = readSavedLayout(storageKey);
    const savedCollapsed = saved.collapsed?.filter(
      (id) => cards.find((card) => card.id === id)?.rememberCollapsed !== false
    );
    return {
      order: saved.order ?? [],
      collapsed: savedCollapsed ?? cards.filter((card) => card.collapsedByDefault).map((card) => card.id)
    };
  });
  const saveLayout = (nextLayout) => {
    setLayout(nextLayout);
    try {
      localStorage.setItem(storageKey, JSON.stringify(nextLayout));
    } catch {
      // Storage full or blocked: the layout still applies, it just won't be remembered
    }
  };
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const cardIds = cards.map((card) => card.id);
  const order = [
    ...layout.order.filter((id) => cardIds.includes(id)),
    ...cardIds.filter((id) => !layout.order.includes(id))
  ];
  const shownCards = order.map((id) => cards.find((card) => card.id === id)).filter((card) => card.show);

  // A card that doesn't remember its collapse also reopens whenever it reappears, e.g. new errors after a re-rate
  const shownIds = shownCards.map((card) => card.id);
  const previouslyShownIds = useRef(shownIds);
  useEffect(() => {
    const reappearedIds = shownCards
      .filter((card) => card.rememberCollapsed === false && !previouslyShownIds.current.includes(card.id))
      .map((card) => card.id);
    previouslyShownIds.current = shownIds;
    if (reappearedIds.some((id) => layout.collapsed.includes(id))) {
      setLayout((current) => ({
        ...current,
        collapsed: current.collapsed.filter((id) => !reappearedIds.includes(id))
      }));
    }
  });

  const onDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      saveLayout({ ...layout, order: arrayMove(order, order.indexOf(active.id), order.indexOf(over.id)) });
    }
  };
  const toggleCollapsed = (id) =>
    saveLayout({
      ...layout,
      collapsed: layout.collapsed.includes(id)
        ? layout.collapsed.filter((collapsedId) => collapsedId !== id)
        : [...layout.collapsed, id]
    });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={visibleCardsCollision}
      onDragEnd={onDragEnd}
      // Keeps dnd-kit's screen-reader text out of the sidebar's flex/grid spacing
      accessibility={{ container: document.body }}
    >
      <SortableContext items={shownCards.map((card) => card.id)} strategy={verticalListSortingStrategy}>
        {shownCards.map((card) => (
          <SortableCard
            key={card.id}
            card={card}
            collapsed={layout.collapsed.includes(card.id)}
            collapsedClassName={collapsedClassName}
            itemComponent={itemComponent}
            onToggleCollapsed={() => toggleCollapsed(card.id)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default SortableSidebar;
